import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { SseClientSubscriber } from '../sse/sse-client.subscriber';
import { defaultSseOptions, SseOptions } from '../sse/types/sse-options';
import { defaultRequestOptions } from '../sse/types/sse-request-options';
import { SseStreamParams } from '../sse/types/sse-stream-params';
import {
  ConnectionError,
  ForbiddenError,
  NetworkError,
  NotFoundError,
  RequestError,
  ServerError,
  UnauthorizedError,
  UnknownError,
  UnknownResponseError,
} from '../types/errors';
import {
  createHttpResourceLocation,
  HttpLocationParameters,
  HttpResourceLocation,
  isDefined,
} from '@softline/core';
import { SseResourceLocation } from './types/sse.location';
import { ConnectionService } from '../connection/connection.service';

const ERROR_CODE_BAD_REQUEST = 400;
const ERROR_CODE_UNAUTHORIZED = 401;
const ERROR_CODE_FORBIDDEN = 403;
const ERROR_CODE_NOT_FOUND = 404;

const ERROR_CODE_SERVER_INTERNAL = 500;
const ERROR_CODE_SERVER_NOT_IMPLEMENTED = 501;
const ERROR_CODE_SERVER_BAD_GATEWAY = 502;
const ERROR_CODE_SERVER_UNAVAILABLE = 503;
const ERROR_CODE_SERVER_GATEWAY_TIMEOUT = 504;

@Injectable({ providedIn: 'root' })
export class SseDataAccess {
  private httpClient = inject(HttpClient);
  private connectionService = inject(ConnectionService);

  stream(params: SseStreamParams): Observable<string | Event> {
    const sseOptions: SseOptions = Object.assign(
      {},
      defaultSseOptions,
      params?.options ?? {}
    );

    const connection = this.convertLocation({
      path: params.path,
      queryParams: params?.queryParams,
      pathParams: params?.pathParams,
    });

    const httpClientOptions = {
      ...defaultRequestOptions,
      ...(params?.requestOptions ?? {}),
      queryParams: connection?.queryParams,
    };

    console.log('httpClientOptions: ', httpClientOptions);

    return new SseClientSubscriber(
      this.httpClient,
      sseOptions,
      httpClientOptions,
      connection.url,
      params?.method ?? 'GET'
    )
      .createObservable()
      .pipe(
        catchError((e) => {
          const error = this.mapError(e);
          return throwError(() => error);
        })
      );
  }

  private convertLocation(params: SseResourceLocation | string): HttpResourceLocation {
    const connection = this.connectionService.connection();

    const path = isDefined(connection.basePath) ? [connection.basePath] : [];

    if (typeof params === 'string') path.push(params);
    else if (Array.isArray(params.path)) path.push(...params.path);
    else if (typeof params.path === 'string') path.push(params.path);

    const httpLocationParams: HttpLocationParameters = {
      host: connection.host,
      port: connection.port,
      path,
    };

    if (typeof params === 'object') {
      httpLocationParams.queryParams = params.queryParams;
      httpLocationParams.pathParams = params.pathParams;
    }

    return createHttpResourceLocation(httpLocationParams);
  }

  private mapError(error: any): Error {
    if (error.error instanceof ErrorEvent) {
      return new NetworkError('A client-side network error occurred', error);
    } else if (error instanceof HttpErrorResponse) {
      let message: string | undefined = undefined;
      let code: string | undefined = undefined;

      if (error.error?.errors && error.error?.errors[0]?.message) {
        message = error.error?.errors[0]?.message;
        code = error.error?.errors[0]?.code;
      }

      switch (error.status) {
        case 0:
          return new ConnectionError(
            'Cannot establish a connection to the server',
            error
          );
        case ERROR_CODE_BAD_REQUEST:
          return new RequestError(
            `HTTP ${error.status} - ${error.statusText}: ${message} (${code})`,
            message,
            code ?? 'No code',
            error
          );
        case ERROR_CODE_UNAUTHORIZED:
          return new UnauthorizedError(
            `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            error
          );
        case ERROR_CODE_FORBIDDEN:
          return new ForbiddenError(
            `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            message ?? `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            code ?? 'No code',
            error
          );
        case ERROR_CODE_NOT_FOUND:
          return new NotFoundError(
            `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            error
          );
        case ERROR_CODE_SERVER_INTERNAL:
        case ERROR_CODE_SERVER_NOT_IMPLEMENTED:
        case ERROR_CODE_SERVER_BAD_GATEWAY:
        case ERROR_CODE_SERVER_UNAVAILABLE:
        case ERROR_CODE_SERVER_GATEWAY_TIMEOUT:
          return new ServerError(
            `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            message ?? `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            code ?? 'No code',
            error
          );
        default:
          return new UnknownResponseError(
            `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            message ?? `HTTP ${error.status} - ${error.statusText}: ${error.message}`,
            code ?? 'No code',
            error
          );
      }
    } else if (error instanceof Error) {
      return new UnknownError('An unexpected error occurred: ' + error.message, error);
    } else {
      return new UnknownError('An unexpected error occurred: ' + error, error);
    }
  }
}
